/**
 * Tomi-no-Kami (富の神) Frontend Service
 * API client for the quantitative trading engine dashboard
 */

import api from './api';

// ─── Types ──────────────────────────────────────────────

export interface TomiStatus {
  engine: string;
  version: string;
  coinbase: {
    connected: boolean;
    dryRun: boolean;
    tradeCount: number;
    maxTrades: number;
    remaining: number;
    tradeLog: TradeRecord[];
  };
  futuresBalance: FuturesBalanceData | null;
  timestamp: string;
}

export interface FuturesBalanceData {
  futures_buying_power: string;
  total_usd_balance: string;
  cbi_usd_balance: string;
  cfm_usd_balance: string;
  total_open_orders_hold_amount: string;
  unrealized_pnl: string;
  daily_realized_pnl: string;
  initial_margin: string;
  available_margin: string;
  liquidation_threshold: string;
  liquidation_buffer_amount: string;
  liquidation_buffer_percentage: string;
}

export interface FuturesPosition {
  product_id: string;
  side: 'LONG' | 'SHORT';
  number_of_contracts: string;
  current_price: string;
  avg_entry_price: string;
  unrealized_pnl: string;
  expiry: string;
}

export interface CoinbaseOrder {
  order_id: string;
  product_id: string;
  side: 'BUY' | 'SELL';
  status: string;
  time_in_force: string;
  created_time: string;
  completion_percentage: string;
  filled_size: string;
  average_filled_price: string;
  total_value_after_fees: string;
  order_type: string;
  order_configuration: Record<string, unknown>;
}

export interface TradeRecord {
  id: string;
  timestamp: string;
  product_id: string;
  side: 'BUY' | 'SELL';
  size: string;
  price: string;
  status: string;
  pnl?: string;
  order_id: string;
}

export interface OrderRequest {
  product_id: string;
  side: 'BUY' | 'SELL';
  size: string;
  leverage?: string;
  order_type: 'market' | 'limit';
  limit_price?: string;
}

export interface TradeData {
  coinbaseOrders: CoinbaseOrder[];
  localTradeLog: TradeRecord[];
  tradeCount: number;
  maxTrades: number;
  remaining: number;
}

export interface TomiSignal {
  asset: string;           // 'BTC-USD'
  symbol: string;          // 'BTC'
  name: string;            // 'Bitcoin'
  action: string;          // 'BUY' | 'SELL' | 'ABSTAIN'
  dominion_score: number;  // 0-1
  kaze_score: number;      // 0-1
  combined_conviction: number;
  signal_class: string;    // 'I', 'II', 'III', 'IV'
  position_size: number;
  entry_price: number;
  stop_loss: number;
  target: number;
  risk_reward: number;
  kaze_narrative: string;
  kaze_edge_type: string;
  kaze_catalyst: string;
  kaze_consensus: string;
  fib_ratio: number;       // 0.382, 0.236, etc.
  fib_label: string;       // 'Primary (38.2%)', etc.
  dollar_allocation: number; // in USD
  timestamp: string;
  // Extended fields for equity signals
  asset_class?: 'crypto' | 'equity';
  broker?: 'coinbase' | 'alpaca';
  sector?: string;
  rs_composite?: number;
}

export interface AlpacaAccount {
  id: string;
  account_number: string;
  status: string;
  currency: string;
  buying_power: string;
  cash: string;
  portfolio_value: string;
  equity: string;
  last_equity: string;
}

export interface AlpacaPosition {
  asset_id: string;
  symbol: string;
  exchange: string;
  asset_class: string;
  qty: string;
  qty_available: string;
  side: string;
  market_value: string;
  cost_basis: string;
  unrealized_pl: string;
  unrealized_plpc: string;
  current_price: string;
  avg_entry_price: string;
}

export interface EquityOrderRequest {
  symbol: string;
  side: 'buy' | 'sell';
  notional?: string;
  qty?: string;
  type?: string;
  time_in_force?: string;
  limit_price?: string;
}

export interface SignalsData {
  signals: TomiSignal[];
  capital: number;
  total_allocated: number;
  cash_reserve: number;
  fib_ratios: number[];
  kaze_connected: boolean;
  tomi_evaluated: number;
  generated_at: string;
}

export interface SpotPortfolio {
  spot: Array<{
    uuid: string;
    name: string;
    currency: string;
    available_balance: { value: string; currency: string };
    hold: { value: string; currency: string };
    type: string;
  }>;
  futures: FuturesBalanceData | null;
  tradeCount: number;
  maxTrades: number;
  remaining: number;
  timestamp: string;
}

export interface SpotOrderRequest {
  product_id: string;
  side: 'BUY' | 'SELL';
  quote_size: string;
  order_type?: 'market' | 'limit';
  limit_price?: string;
}

export interface OptionsSignal extends TomiSignal {
  contract_symbol: string | null;  // OCC format e.g. VG260418C00016000
  option_type: 'call' | 'put';
  strike: number;
  expiry: string;            // YYYY-MM-DD
  dte: number;               // days to expiry
  ask_price: number;         // premium per share
  mid_price: number;
  contract_cost: number;     // ask * 100 per contract
  contracts: number;         // number of contracts to buy
  total_premium: number;     // total dollars deployed
  stop_premium: number;      // 50% loss threshold (per contract)
  target_1x: number;         // 100% gain threshold (per contract)
  target_2x: number;         // 200% gain threshold (per contract)
  delta: number | null;
  implied_vol: number | null;
  has_options: boolean;
}

export interface SignalFeedback {
  asset: string;
  action: string;
  entry_price: number;
  exit_price: number;
  outcome: 'win' | 'loss' | 'breakeven';
  notes?: string;
}

export interface LearningStats {
  total_signals: number;
  active_positions: number;
  wins: number;
  losses: number;
  win_rate: number | null;
  avg_win_pct: number | null;
  avg_loss_pct: number | null;
  unrealized_pl: number;
  market_value: number;
  by_asset: Record<string, { wins: number; losses: number; total: number; active: number }>;
  active_signals: ActiveSignal[];
  recent: SignalFeedback[];
}

export interface ActiveSignal {
  id: string;
  timestamp: string;
  asset: string;
  action: string;
  entry_price: number;
  pnl_pct: number;
  market_value: number;
  unrealized_pl: number;
  asset_class: string;
  source: string;
}

export type AutopilotMode = 'conservative' | 'standard' | 'aggressive';
export type AutopilotStatus = 'idle' | 'running' | 'paused' | 'stopping';

export interface AutopilotDecision {
  id: string;
  timestamp: string;
  type: 'scan' | 'enter' | 'exit' | 'skip' | 'monitor' | 'error';
  symbol: string;
  asset_class: 'equity' | 'crypto' | 'option';
  action: string;
  conviction: number;
  kaze_score: number;
  reason: string;
  allocation?: number;
  contracts?: number;
  order_id?: string;
  executed: boolean;
  error?: string;
}

export interface AutopilotSession {
  id: string;
  started_at: string;
  stopped_at?: string;
  config: {
    mode: AutopilotMode;
    capital: number;
    max_positions: number;
    scan_interval_ms: number;
    markets: string[];
    use_options: boolean;
    stop_loss_pct: number;
    take_profit_pct: number;
    min_conviction: number;
  };
  status: AutopilotStatus;
  cycles: number;
  decisions: AutopilotDecision[];
  last_scan?: string;
  next_scan?: string;
  posture?: {
    regime: 'offensive' | 'neutral' | 'defensive' | 'preservation';
    max_deploy_pct: number;
    max_positions: number;
    min_conviction: number;
    allow_new_entries: boolean;
    short_bias: boolean;
    reason: string;
  };
  stats: {
    entries: number;
    exits: number;
    skipped: number;
    errors: number;
    total_deployed: number;
    unrealized_pl: number;
    realized_pl: number;
    wins: number;
    losses: number;
  };
}

// ─── API Calls ──────────────────────────────────────────

const tomiServiceBase = {
  /** Get engine status, connection health, and balance */
  async getStatus(): Promise<TomiStatus> {
    const res = await api.get('/tomi/status');
    return res.data.data;
  },

  /** Get current futures positions */
  async getPositions(): Promise<{ positions: FuturesPosition[]; balance: FuturesBalanceData | null; count: number }> {
    const res = await api.get('/tomi/positions');
    return res.data.data;
  },

  /** Place a futures order */
  async placeOrder(order: OrderRequest): Promise<CoinbaseOrder | null> {
    const res = await api.post('/tomi/orders', order);
    return res.data.data;
  },

  /** Cancel an order */
  async cancelOrder(orderId: string): Promise<boolean> {
    const res = await api.delete(`/tomi/orders/${orderId}`);
    return res.data.success;
  },

  /** Get a specific order */
  async getOrder(orderId: string): Promise<CoinbaseOrder | null> {
    const res = await api.get(`/tomi/orders/${orderId}`);
    return res.data.data;
  },

  /** Get trade history (Coinbase + local log) */
  async getTrades(limit: number = 50): Promise<TradeData> {
    const res = await api.get(`/tomi/trades?limit=${limit}`);
    return res.data.data;
  },

  /** Get all accounts */
  async getAccounts(): Promise<unknown[]> {
    const res = await api.get('/tomi/accounts');
    return res.data.data;
  },

  /** Get market data for a product */
  async getMarketData(productId: string, granularity: string = 'ONE_HOUR'): Promise<{ product: unknown; candles: unknown[] }> {
    const res = await api.get(`/tomi/market/${productId}?granularity=${granularity}`);
    return res.data.data;
  },

  /** Reset the 100-trade counter */
  async resetTradeCounter(): Promise<unknown> {
    const res = await api.post('/tomi/reset-counter', {});
    return res.data.data;
  },

  /** Analyze a DCA futures strategy using historical performance */
  async analyzeDca(params: DcaAnalysisRequest): Promise<DcaAnalysisResult> {
    const res = await api.post('/tomi/analyze-dca', params);
    return res.data.data;
  },
};

export interface DcaAnalysisRequest {
  capital: number;
  target_price: number;
  current_price: number;
  contracts_per_tranche: number;
  tranches: number;
  product_id: string;
}

export interface DcaAnalysisResult {
  historical: {
    total_orders: number;
    filled_orders: number;
    round_trips: number;
    win_rate: number;
    btc_win_rate: number;
    avg_win: number;
    avg_loss: number;
    total_net_pnl: number;
    total_fees: number;
    total_volume: number;
    profit_factor: number | string;
    expectancy_per_trade: number;
    trades: Array<{
      direction: string;
      entry_price: number;
      exit_price: number;
      pnl: number;
      net_pnl: number;
      pnl_pct: number;
      product: string;
    }>;
  };
  strategy: {
    name: string;
    product_id: string;
    capital: number;
    current_price: number;
    target_price: number;
    upside_pct: number;
    tranches: number;
    contracts_per_tranche: number;
    total_contracts: number;
    total_capital_deployed: number;
    avg_entry_price: number;
    dca_levels: Array<{
      tranche: number;
      entry_price: number;
      contracts: number;
      capital_deployed: number;
    }>;
    projected_gross_pnl: number;
    projected_net_pnl: number;
    projected_roi_pct: number;
    estimated_fees: number;
    liquidation_price: number;
    scenarios: Array<{
      label: string;
      price: number;
      pnl: number;
    }>;
  };
  recommendation: {
    verdict: string;
    rationale: string;
    risk_factors: string[];
  };
  timestamp: string;
}

export interface AgentChatMessage {
  id: string;
  agent: 'tomi' | 'kaze' | 'user';
  content: string;
  timestamp: string;
}

const tomiService = {
  ...tomiServiceBase,

  async getChat(): Promise<AgentChatMessage[]> {
    try {
      const res = await api.get('/tomi/chat');
      return res.data?.data || res.data || [];
    } catch {
      return [];
    }
  },

  async sendChat(message: string): Promise<AgentChatMessage[]> {
    const res = await api.post('/tomi/chat', { message });
    return res.data?.data || res.data || [];
  },

  async triggerChatExchange(initiator: 'tomi' | 'kaze' = 'kaze'): Promise<AgentChatMessage[]> {
    const res = await api.post('/tomi/chat', { initiator });
    return res.data?.data || res.data || [];
  },

  async clearChat(): Promise<void> {
    await api.delete('/tomi/chat');
  },

  /** Generate Tomi trading signals with Fibonacci allocation */
  async getSignals(capital: number = 200): Promise<SignalsData> {
    const res = await api.get(`/tomi/signals?capital=${capital}`);
    return res.data.data;
  },

  /** Get current spot crypto portfolio */
  async getPortfolio(): Promise<SpotPortfolio> {
    const res = await api.get('/tomi/portfolio');
    return res.data.data;
  },

  /** Place a spot crypto order */
  async placeSpotOrder(order: SpotOrderRequest): Promise<CoinbaseOrder | null> {
    const res = await api.post('/tomi/spot-order', order);
    return res.data.data;
  },

  /** Record signal outcome for Tomi learning */
  async recordFeedback(feedback: SignalFeedback): Promise<unknown> {
    const res = await api.post('/tomi/signals/feedback', feedback);
    return res.data.data;
  },

  /** Get Tomi learning statistics */
  async getLearningStats(): Promise<LearningStats> {
    const res = await api.get('/tomi/signals/learning');
    return res.data.data;
  },

  // ─── Equity (Alpaca) ──────────────────────────────────

  /** Generate Tomi equity signals from Kaze NDW + Yahoo Finance */
  async getEquitySignals(capital: number = 200): Promise<SignalsData> {
    const res = await api.get(`/equity/signals?capital=${capital}`);
    return res.data.data;
  },

  /** Alpaca account status and trade cap */
  async getEquityStatus(): Promise<{ broker: string; account: AlpacaAccount | null; status: unknown; timestamp: string }> {
    const res = await api.get('/equity/status');
    return res.data.data;
  },

  /** Current equity positions held in Alpaca */
  async getEquityPositions(): Promise<{ positions: AlpacaPosition[]; count: number; total_market_value: number; total_unrealized_pl: number; buying_power: string }> {
    const res = await api.get('/equity/positions');
    return res.data.data;
  },

  /** Place an equity order via Alpaca (notional = dollar amount for fractional) */
  async placeEquityOrder(order: EquityOrderRequest): Promise<unknown> {
    const res = await api.post('/equity/orders', order);
    return res.data.data;
  },

  // ─── Options (Alpaca) ─────────────────────────────────

  /** Generate Tomi options signals — OTM calls on top equity BUY signals, $100k capital */
  async getOptionsSignals(capital: number = 100000): Promise<SignalsData & { signals: OptionsSignal[] }> {
    const res = await api.get(`/equity/options/signals?capital=${capital}`);
    return res.data.data;
  },

  /** Place an options order via Alpaca */
  async placeOptionsOrder(order: {
    contract_symbol: string;
    qty: number;
    side?: 'buy' | 'sell';
    order_type?: 'market' | 'limit';
    limit_price?: string;
  }): Promise<unknown> {
    const res = await api.post('/equity/options/orders', order);
    return res.data.data;
  },

  /** Get options chain for a symbol */
  async getOptionsChain(symbol: string): Promise<unknown> {
    const res = await api.get(`/equity/options/chain/${symbol}`);
    return res.data.data;
  },

  /** Get current options positions */
  async getOptionsPositions(): Promise<{ positions: AlpacaPosition[]; count: number; total_market_value: number; total_unrealized_pl: number }> {
    const res = await api.get('/equity/options/positions');
    return res.data.data;
  },

  // ─── Autopilot ──────────────────────────────────────

  async startAutopilot(config: { mode: AutopilotMode; capital?: number; markets?: string[]; use_options?: boolean; use_shorts?: boolean; use_stacked_entries?: boolean; use_bracket_orders?: boolean }): Promise<AutopilotSession> {
    const res = await api.post('/tomi/autopilot/start', config);
    return res.data.data;
  },

  async stopAutopilot(): Promise<AutopilotSession> {
    const res = await api.post('/tomi/autopilot/stop', {});
    return res.data.data;
  },

  async getAutopilotStatus(): Promise<{ running: boolean; session: AutopilotSession | null; portfolio: { cash: number; portfolio_value: number; positions: number; market_value: number; unrealized_pl: number } | null }> {
    const res = await api.get('/tomi/autopilot/status');
    return res.data.data;
  },
};

export default tomiService;
